import { TFunction } from 'i18next';
import moment from 'moment';

export interface DateRangePickerOptions {
  parentEl?: HTMLElement;
  template?: string | Element;
  locale?: {
    direction?: string;
    format?: string;
    separator?: string;
    applyLabel?: string;
    cancelLabel?: string;
    weekLabel?: string;
    customRangeLabel?: string;
    daysOfWeek?: string[];
    monthNames?: string[];
    firstDay?: number;
  };
  startDate?: string | Date | moment.Moment;
  endDate?: string | Date | moment.Moment;
  minDate?: string | Date | moment.Moment;
  maxDate?: string | Date | moment.Moment;
  maxSpan?: moment.Duration;
  showDropdowns?: boolean;
  minYear?: number;
  maxYear?: number;
  showWeekNumbers?: boolean;
  showISOWeekNumbers?: boolean;
  timePicker?: boolean;
  timePicker24Hour?: boolean;
  timePickerIncrement?: number;
  timePickerSeconds?: boolean;
  ranges?: { [key: string]: [moment.Moment, moment.Moment] };
  opens?: 'left' | 'right' | 'center';
  drops?: 'down' | 'up';
  buttonClasses?: string | string[];
  applyButtonClasses?: string;
  cancelButtonClasses?: string;
  autoApply?: boolean;
  singleDatePicker?: boolean;
  autoUpdateInput?: boolean;
  linkedCalendars?: boolean;
  isInvalidDate?: (date: moment.Moment) => boolean;
  isCustomDate?: (date: moment.Moment) => boolean | string | string[];
  alwaysShowCalendars?: boolean;
  dateLimit?: moment.Duration;
  inline?: boolean;
  onClick?: (picker: DateRangePicker) => void;
  showRangeInputs?: boolean;
  selectionMode?: 'date' | 'month' | 'year' | 'week' | 'range';
  withSelectionTypes?: boolean;
  showTwoCalendars?: boolean;
  DayRangePicker?: boolean;
  disabled?: boolean;
  onChange?: (start: moment.Moment, end: moment.Moment) => void;
  onHoverDateChange?: (date: moment.Moment | null) => void;
  hoverStartDate?: moment.Moment | null;
  hoverEndDate?: moment.Moment | null;
  displayFormat?: string;
  t: TFunction<['translation', ...string[]], undefined>;
}

interface Calendar {
  month: moment.Moment;
  calendar?: moment.Moment[][];
}

export class DateRangePicker {
  public parentEl: HTMLElement | undefined;
  public element: HTMLElement;
  public container: HTMLElement;
  /** Legacy-scoped host wrapping the portaled panel — see where it's created. */
  private legacyHost?: HTMLElement;
  public startDate: moment.Moment;
  public endDate: moment.Moment;
  public minDate: moment.Moment | null;
  public maxDate: moment.Moment | null;
  public maxSpan: moment.Duration | null;
  public autoApply: boolean;
  public singleDatePicker: boolean;
  public showDropdowns: boolean;
  public minYear: string;
  public maxYear: string;
  public showWeekNumbers: boolean;
  public showISOWeekNumbers: boolean;
  public showCustomRangeLabel: boolean;
  public timePicker: boolean;
  public timePicker24Hour: boolean;
  public timePickerIncrement: number;
  public timePickerSeconds: boolean;
  public linkedCalendars: boolean;
  public autoUpdateInput: boolean;
  public alwaysShowCalendars: boolean;
  public ranges: { [key: string]: [moment.Moment, moment.Moment] };
  public opens: string;
  public drops: string;
  public buttonClasses: string;
  public applyButtonClasses: string;
  public cancelButtonClasses: string;
  public disabled: boolean;
  public locale: {
    direction: string;
    format: string;
    separator: string;
    applyLabel: string;
    cancelLabel: string;
    weekLabel: string;
    customRangeLabel: string;
    daysOfWeek: string[];
    monthNames: string[];
    firstDay: number;
  };
  public displayFormat?: string;
  public callback: (
    start?: moment.Moment,
    end?: moment.Moment,
    label?: string,
  ) => void;
  public isShowing: boolean;
  public leftCalendar: Calendar;
  public rightCalendar: Calendar;
  public previousRightTime: moment.Moment;
  public oldStartDate: moment.Moment;
  public oldEndDate: moment.Moment;
  public chosenLabel: string;
  public _outsideClick: (e: Event) => void;
  public _handleResizeProxy: () => void;
  public inline: boolean;
  public showRangeInputs: boolean;
  public selectionMode: 'date' | 'month' | 'year' | 'week' | 'range';
  public withSelectionTypes: boolean;
  public showTwoCalendars: boolean;
  public DayRangePicker: boolean = false;
  public selectionTypes: {
    label: string;
    value: 'date' | 'month' | 'year' | 'range' | 'week';
  }[];
  public t: TFunction<['translation', ...string[]], undefined>;
  public onChange: (start: moment.Moment, end: moment.Moment) => void;
  public onHoverDateChange: (date: moment.Moment | null) => void;
  public onClick?: (picker: DateRangePicker) => void;
  public hoverStartDate: moment.Moment | null = null;
  public hoverEndDate: moment.Moment | null = null;
  constructor(
    element: HTMLElement,
    options: DateRangePickerOptions = {},
    cb?: (start?: moment.Moment, end?: moment.Moment, label?: string) => void,
  ) {
    //default settings for options
    this.parentEl = options.parentEl ? options.parentEl : document.body;
    this.element = element;
    this.startDate = moment().startOf('day');
    this.endDate = moment().endOf('day');
    this.minDate = null;
    this.maxDate = null;
    this.maxSpan = null;
    this.autoApply = false;
    this.singleDatePicker =
      options.selectionMode == 'month' ||
      options.selectionMode == 'year' ||
      options.selectionMode == 'date' ||
      options.singleDatePicker ||
      false;
    this.showDropdowns = false;
    this.minYear = moment().subtract(100, 'year').format('YYYY');
    this.maxYear = moment().add(100, 'year').format('YYYY');
    this.showWeekNumbers = false;
    this.showISOWeekNumbers = false;
    this.showCustomRangeLabel = true;
    this.timePicker = false;
    this.timePicker24Hour = false;
    this.timePickerIncrement = 1;
    this.timePickerSeconds = false;
    this.linkedCalendars = true;
    this.autoUpdateInput = true;
    this.alwaysShowCalendars = false;
    this.ranges = {};
    this.opens = 'right';
    this.drops = 'auto';
    this.buttonClasses = 'btn btn-sm';
    this.applyButtonClasses = 'btn-primary';
    this.cancelButtonClasses = 'btn-default';
    this.disabled = options.disabled || false;
    this.chosenLabel = '';
    this._outsideClick = (e: Event): void => {
      const target = e.target as HTMLElement;
      if (
        // If click is outside the datepicker container and input element
        !this.container.contains(target) &&
        !this.element.contains(target) &&
        this.isShowing
      ) {
        this.clickCancel(e);
      }
    };
    this._handleResizeProxy = this.handleResize.bind(this);
    this.t = options.t;
    const days = moment.weekdaysMin();
    const daysStartMonday = days.slice(1).concat(days.slice(0, 1));
    const rtlLocales = ['ar'];
    const direction = rtlLocales.includes(moment.locale()) ? 'rtl' : 'ltr';
    this.locale = {
      direction: direction,
      format: options.displayFormat ?? 'DD/MM/YYYY',
      separator: ' - ',
      applyLabel: 'Apply',
      cancelLabel: 'Cancel',
      weekLabel: 'W',
      customRangeLabel: 'Custom Range',
      daysOfWeek: daysStartMonday,
      monthNames: moment.monthsShort(),
      firstDay: 1,
    };

    this.callback = cb || (() => {});
    this.isShowing = false;
    this.leftCalendar = {} as Calendar;
    this.rightCalendar = {} as Calendar;
    this.previousRightTime = this.endDate.clone();
    this.oldStartDate = this.startDate.clone();
    this.oldEndDate = this.endDate.clone();
    this.inline = options.inline || false;
    this.onClick = options.onClick || (() => {});
    this.showRangeInputs = true;
    this.selectionMode = options.selectionMode || 'date';
    this.withSelectionTypes = options.withSelectionTypes || false;
    this.onChange = options.onChange || (() => {});
    this.onHoverDateChange = options.onHoverDateChange || (() => {});
    this.showTwoCalendars =
      options.withSelectionTypes ||
      options.singleDatePicker ||
      options.selectionMode == 'month' ||
      options.selectionMode == 'year' ||
      options.selectionMode == 'date' ||
      options.DayRangePicker ||
      !(options.ranges && Object.keys(options.ranges).length > 0)
        ? false
        : true;
    this.selectionTypes = [
      {
        label: 'date.Quotidienne',
        value: 'date',
      },
      {
        label: 'date.Hebdomadaire',
        value: 'week',
      },
      {
        label: 'date.Mensuelle',
        value: 'month',
      },
      {
        label: 'date.Annuelle',
        value: 'year',
      },
      {
        label: 'date.Personnalise',
        value: 'range',
      },
    ];

    // Apply options
    if (typeof options === 'object' && options !== null) {
      const defaultLocale = this.locale;
      // Copy options to instance
      Object.assign(this, options);
      if (typeof this.onHoverDateChange !== 'function') {
        this.onHoverDateChange = () => {};
      }
      if (typeof this.onChange !== 'function') {
        this.onChange = () => {};
      }
      this.hoverStartDate = options.hoverStartDate || null;
      this.hoverEndDate = options.hoverEndDate || null;
      this.onClick = options.onClick;

      // Special handling for dates
      if (typeof options.startDate === 'string') {
        this.startDate = moment(options.startDate, this.locale.format);
      } else if (
        options.startDate instanceof Date ||
        moment.isMoment(options.startDate)
      ) {
        this.startDate = moment(options.startDate);
      }

      if (typeof options.endDate === 'string') {
        this.endDate = moment(options.endDate, this.locale.format);
      } else if (
        options.endDate instanceof Date ||
        moment.isMoment(options.endDate)
      ) {
        this.endDate = moment(options.endDate);
      }

      if (typeof options.minDate === 'string') {
        this.minDate = moment(options.minDate, this.locale.format);
      } else if (
        options.minDate instanceof Date ||
        moment.isMoment(options.minDate)
      ) {
        this.minDate = moment(options.minDate);
      }

      if (typeof options.maxDate === 'string') {
        this.maxDate = moment(options.maxDate, this.locale.format);
      } else if (
        options.maxDate instanceof Date ||
        moment.isMoment(options.maxDate)
      ) {
        this.maxDate = moment(options.maxDate);
      }

      if (options.ranges) {
        this.ranges = {};
        for (const [key, range] of Object.entries(options.ranges)) {
          this.ranges[key] = [moment(range[0]), moment(range[1])];
        }
      }

      if (options.locale) {
        this.locale = { ...defaultLocale, ...options.locale };
      }
    }

    // Create container element
    console.log('options', options.ranges);
    this.container = document.createElement('div');
    this.container.className = 'daterangepicker';

    if (this.DayRangePicker) {
      this.container.classList.add('datepicker-m');
    }
    if (this.inline) {
      this.container.classList.add('inline');
    }
    this.container.innerHTML = this.buildTemplate();
    if (this.ranges) {
      this.renderRanges();
    }

    if (this.withSelectionTypes) {
      this.renderSelectionTypes();
    }

    // When portaled (parentEl = document.body), the panel lands OUTSIDE the
    // `data-route="legacy"` subtree that scopes legacy CSS on kit routes. Every
    // rule written `.daterangepicker …` (which is most of datepicker.css —
    // selected-day colour, arrows, spacing) needs that attribute on an
    // ANCESTOR, so without one the panel falls back to the library's raw look
    // (blue selected day, unstyled arrows). Wrapping it in a legacy-marked host
    // gives those rules the ancestor they need.
    //
    // `position: static` is set explicitly so the legacy `* { position: relative }`
    // reset can't turn the host into a containing block on legacy routes — the
    // library positions the panel absolutely in document coordinates, so the
    // host must stay transparent to layout.
    //
    // Inline pickers already render inside the (legacy) form, so they're left
    // alone. Remove once these drawers are migrated to kit-native Drawers.
    if (this.parentEl === document.body) {
      this.legacyHost = document.createElement('div');
      this.legacyHost.setAttribute('data-route', 'legacy');
      this.legacyHost.style.position = 'static';
      this.legacyHost.appendChild(this.container);
      this.parentEl.appendChild(this.legacyHost);
    } else {
      this.parentEl.appendChild(this.container);
    }

    this.setupEventListeners();
    this.updateView();

    if (this.inline) {
      this.container.style.display = 'flex';
      this.container.classList.add('inline');
      this.container.style.position = 'relative';
      this.container.classList.add('show');

      const leftCalendar = this.container.querySelector(
        '.drp-calendar.left',
      ) as HTMLElement;
      const rightCalendar = this.container.querySelector(
        '.drp-calendar.right',
      ) as HTMLElement;

      if (
        !this.withSelectionTypes &&
        ((this.showTwoCalendars && !this.singleDatePicker) ||
          (options.ranges && Object.keys(options.ranges).length > 0)) &&
        !this.DayRangePicker
      ) {
        if (leftCalendar) leftCalendar.style.display = 'flex';
        if (rightCalendar) rightCalendar.style.display = 'flex';
      } else {
        if (leftCalendar) leftCalendar.style.display = 'flex';
        if (rightCalendar) rightCalendar.style.display = 'none';
      }
    }
    if (this.withSelectionTypes) {
      this.container.classList.add('ranges');
      const rightCalendar = this.container.querySelector(
        '.drp-calendar.right',
      ) as HTMLElement;
      if (rightCalendar) rightCalendar.style.display = 'none ';
    }
    if (this.singleDatePicker) {
      this.enabledApplyButton();
    }
  }

  private buildTemplate(): string {
    return `
      <div class="ranges"></div>
      <div class="drp-calendar left">
        <div class="calendar-table"></div>
        <div class="calendar-time"></div>
      </div>
      <div class="drp-calendar right" >
        <div class="calendar-table"></div>
        <div class="calendar-time"></div>
      </div>
      <div class="drp-buttons">
        <span class="drp-selected"></span>
        <button class="cancelBtn" type="button">${this.locale.cancelLabel}</button>
        <button class="applyBtn" disabled="disabled" type="button">${this.locale.applyLabel}</button>
      </div>
    `;
  }

  private setupEventListeners(): void {
    // Calendar navigation
    this.container.querySelectorAll('.drp-calendar').forEach((calendar) => {
      calendar.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;
        if (
          target.parentElement?.classList.contains('prev') ||
          target.classList.contains('prev')
        ) {
          e.stopPropagation();
          this.clickPrev(e);
        }
        if (
          target.parentElement?.classList.contains('next') ||
          target.classList.contains('next')
        ) {
          e.stopPropagation();
          this.clickNext(e);
        }
      });

      calendar.addEventListener('mousedown', (e: Event) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('available')) this.clickDate(e);
      });

      // Add mouseover and mouseout events for date hover effects
      calendar.addEventListener('mouseover', (e: Event) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('available')) {
          this.hoverDate(e);
        }
      });

      calendar.addEventListener('mouseout', (e: Event) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('available')) {
          target.classList.remove('hover-state');
          this.onHoverDateChange(null);
        }
      });

      calendar.addEventListener('change', (e: Event) => {
        const target = e.target as HTMLElement;
        if (
          target.classList.contains('yearselect') ||
          target.classList.contains('monthselect')
        ) {
          this.monthOrYearChanged(e);
        }
        if (
          target.classList.contains('hourselect') ||
          target.classList.contains('minuteselect') ||
          target.classList.contains('secondselect') ||
          target.classList.contains('ampmselect')
        ) {
          this.timeChanged(e);
        }
      });
    });

    // Range selection
    const rangesEl = this.container.querySelector('.ranges');

    if (rangesEl) {
      rangesEl.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('custom-range')) {
          document
            .querySelector('.ranges li.active')
            ?.classList.remove('active');
          target.classList.add('active');

          this.setStartDate(moment());
          this.setEndDate(moment());
          this.updateView();
        } else {
          if (target.tagName === 'LI') this.clickRange(e);
        }
      });
    }

    // Apply/Cancel buttons
    const buttonsEl = this.container.querySelector('.drp-buttons');
    if (buttonsEl) {
      buttonsEl.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('applyBtn')) this.clickApply(e);
        if (target.classList.contains('cancelBtn')) this.clickCancel(e);
      });
    }

    // Input element events
    if (this.element.tagName === 'INPUT' || this.element.tagName === 'BUTTON') {
      this.element.addEventListener('click', () => this.show());
      this.element.addEventListener('focus', () => this.show());
      this.element.addEventListener('keyup', () => this.elementChanged());
      this.element.addEventListener('keydown', (e: KeyboardEvent) =>
        this.keydown(e),
      );
    } else {
      this.element.addEventListener('click', () => this.toggle());
      this.element.addEventListener('keydown', () => this.toggle());
    }

    // Add resize listener
    window.addEventListener('resize', this._handleResizeProxy);

    // Custom Select Events
    this.container.addEventListener('click', (e: Event) => {
      e.stopPropagation();
      const target = e.target as HTMLElement;

      // Toggle Dropdown
      const trigger = target.closest('.minthr-custom-select-trigger');
      if (trigger) {
        const container = trigger.closest('.minthr-custom-select-container');
        if (container) {
          // Close other dropdowns
          this.container
            .querySelectorAll('.minthr-custom-select-container.open')
            .forEach((el) => {
              if (el !== container) el.classList.remove('open');
            });
          container.classList.toggle('open');

          // Scroll to selected option
          if (container.classList.contains('open')) {
            const optionsContainer = container.querySelector(
              '.minthr-custom-options',
            ) as HTMLElement;
            const selectedOption = container.querySelector(
              '.minthr-custom-option.selected',
            ) as HTMLElement;

            if (optionsContainer && selectedOption) {
              // Use setTimeout to ensure the container is visible and layout is calculated
              setTimeout(() => {
                optionsContainer.scrollTop =
                  selectedOption.offsetTop - optionsContainer.offsetTop;
              }, 0);
            }
          }
        }
        return;
      }

      // Select Option
      const option = target.closest('.minthr-custom-option');
      if (option && !option.classList.contains('disabled')) {
        e.stopPropagation();
        const container = option.closest('.minthr-custom-select-container');
        if (container) {
          const value = option.getAttribute('data-value');
          const label = option.textContent;

          container.setAttribute('data-value', value || '');
          const textSpan = container.querySelector(
            '.minthr-custom-select-text',
          );
          if (textSpan) textSpan.textContent = label;

          container
            .querySelectorAll('.minthr-custom-option')
            .forEach((opt) => opt.classList.remove('selected'));
          option.classList.add('selected');

          container.classList.remove('open');
          // alert('clickRangeMobile');
          // Trigger change event logic
          if (
            container.classList.contains('monthselect') ||
            container.classList.contains('yearselect')
          ) {
            this.monthOrYearChanged(e, container as HTMLElement);
          } else if (container.classList.contains('rangeselect')) {
            this.clickRangeMobile(e);
          }
        }
      }

      // Close dropdowns if clicked outside
      if (!target.closest('.minthr-custom-select-container')) {
        this.container
          .querySelectorAll('.minthr-custom-select-container.open')
          .forEach((el) => el.classList.remove('open'));
      }
    });
  }

  private disbledApplyButton(): void {
    const applyBtns = this.container.querySelectorAll<HTMLButtonElement>(
      '.drp-buttons .applyBtn',
    );
    applyBtns.forEach((btn) => (btn.disabled = true));
  }

  private enabledApplyButton(): void {
    const applyBtns = this.container.querySelectorAll<HTMLButtonElement>(
      '.drp-buttons .applyBtn',
    );
    applyBtns.forEach((btn) => (btn.disabled = false));
  }

  private clickDate(e: Event): void {
    const element = e.target as HTMLElement;
    // Safeguard: Do not process if click is on navigation arrows
    if (element.closest('.prev') || element.closest('.next')) return;

    const isEnds = element.classList.contains('ends');
    if (isEnds) return;
    const windowWidth = window.innerWidth;
    const isMobile = windowWidth < 768;
    if (this.selectionMode === 'month') {
      const month = element.getAttribute('data-month');
      if (!month) return;
      document
        .querySelector('.available-month.active')
        ?.classList.remove('active');
      element.classList.add('active');
      this.leftCalendar.month = this.leftCalendar.month.clone().month(month);
      this.setStartDate(this.leftCalendar.month.clone().startOf('month'));
      this.setEndDate(this.leftCalendar.month.clone().endOf('month'));
      this.updateCalendars();
      this.updateView();
      if (this.autoApply) {
        this.clickApply(e);
      }
      return;
    } else if (this.selectionMode === 'year') {
      const year = element.getAttribute('data-year');
      if (!year) return;
      document
        .querySelector('.available-year.active')
        ?.classList.remove('active');
      element.classList.add('active');
      this.leftCalendar.month = this.leftCalendar.month
        .clone()
        .year(parseInt(year));
      this.setStartDate(this.leftCalendar.month.clone().startOf('year'));
      this.setEndDate(this.leftCalendar.month.clone().endOf('year'));
      this.updateCalendars();
      this.updateView();
      if (this.autoApply) {
        this.clickApply(e);
      }
    }
    const title = element.getAttribute('data-title');
    if (!title) return;

    const row = parseInt(title.substr(1, 1));
    const col = parseInt(title.substr(3, 1));
    const cal = element.closest('.drp-calendar');
    if (!cal) return;

    const side = cal.classList.contains('left') ? 'left' : 'right';
    const calendar = side === 'left' ? this.leftCalendar : this.rightCalendar;

    if (!calendar.calendar) return;

    let date = calendar.calendar[row][col].clone();

    // If clicking on a date from a different month, update the view first, then allow selection
    if (date.month() !== calendar.month.month()) {
      this.leftCalendar.month = date.clone().startOf('month');

      // Update right calendar to be one month ahead when linkedCalendars is true
      if (this.linkedCalendars) {
        this.rightCalendar.month = this.leftCalendar.month
          .clone()
          .add(1, 'month');
      } else {
        this.rightCalendar.month = date
          .clone()
          .startOf('month')
          .add(1, 'month');
      }

      this.updateCalendars();

      // FIX: After updating the view, now process the date selection
      // Don't return here, continue to select the date
    }

    if (this.endDate || date.isBefore(this.startDate, 'day')) {
      if (this.timePicker) {
        date = this.startDate
          .clone()
          .year(date.year())
          .month(date.month())
          .date(date.date());
      }
      this.endDate = null;
      this.setStartDate(date);

      // Removed legacy onClick call with incorrect arguments
      // if (this.onClick && !this.endDate) {
      //   this.onClick(this.startDate, this.endDate);
      // }

      if (!this.singleDatePicker) {
        this.disbledApplyButton();
      }

      if (this.selectionMode === 'week') {
        const startOfWeek = this.startDate.clone().startOf('week');
        const endOfWeek = this.startDate.clone().endOf('week');
        this.setStartDate(startOfWeek);
        this.setEndDate(endOfWeek);

        if (!isMobile) {
          this.updateView();
          this.clickApply(e);
          return;
        } else {
          this.updateView();
          this.enabledApplyButton();
          return;
        }
      }
    } else if (!this.endDate && date.isBefore(this.startDate)) {
      this.setEndDate(this.startDate.clone());
    } else {
      if (this.timePicker) {
        const hour = this.endDate
          ? (this.endDate as moment.Moment).hour()
          : date.hour();
        const minute = this.endDate
          ? (this.endDate as moment.Moment).minute()
          : date.minute();
        const second = this.endDate
          ? (this.endDate as moment.Moment).second()
          : date.second();
        date = date.clone().hour(hour).minute(minute).second(second);
      }
      this.setEndDate(date);

      this.enabledApplyButton();

      if (this.autoApply) {
        this.calculateChosenLabel();
        this.clickApply(e);
      }
    }

    if (this.singleDatePicker) {
      this.setEndDate(this.startDate);
      if (isMobile) {
        this.enabledApplyButton();
      } else {
        if (this.autoApply) this.clickApply(e);
      }
    }

    if (this.onClick) {
      this.onClick(this);
    }
    this.updateView();
  }

  private clickApply(e: Event): void {
    this.hide();
    e.stopPropagation();
  }

  private clickCancel(e: Event): void {
    // this.startDate = this.oldStartDate;
    // this.endDate = this.oldEndDate;
    if (!this.isShowing || this.inline) return;
    // this.updateElement();
    this.container.classList.remove('show');
    document.removeEventListener('click', this._outsideClick);
    this.container.style.display = 'none';
    this.isShowing = false;
  }

  private monthOrYearChanged(e: Event, changedContainer?: HTMLElement): void {
    const target = (changedContainer || e.target) as HTMLElement;
    const drpCalendar = target.closest('.drp-calendar');
    if (!drpCalendar) return;

    const monthContainer = drpCalendar.querySelector(
      '.monthselect.minthr-custom-select-container',
    );
    const yearContainer = drpCalendar.querySelector(
      '.yearselect.minthr-custom-select-container',
    );

    const month = parseInt(monthContainer?.getAttribute('data-value') || '0');
    const year = parseInt(yearContainer?.getAttribute('data-value') || '0');

    if (!isNaN(month) && !isNaN(year)) {
      // Update left calendar
      this.leftCalendar.month.month(month).year(year);

      // Update right calendar to be one month ahead
      this.rightCalendar.month = this.leftCalendar.month
        .clone()
        .add(1, 'month');

      this.updateCalendars();
    }
  }

  private timeChanged(e: Event): void {
    const target = e.target as HTMLSelectElement;
    const cal = target.closest('.drp-calendar');
    if (!cal) return;

    const side = cal.classList.contains('left') ? 'left' : 'right';
    let date = side === 'left' ? this.startDate : this.endDate;
    if (!date) return;

    const hour = parseInt(
      (cal.querySelector('.hourselect') as HTMLSelectElement)?.value || '0',
    );
    const minute = parseInt(
      (cal.querySelector('.minuteselect') as HTMLSelectElement)?.value || '0',
    );
    const second = parseInt(
      (cal.querySelector('.secondselect') as HTMLSelectElement)?.value || '0',
    );

    if (!this.timePicker24Hour) {
      const ampm =
        (cal.querySelector('.ampmselect') as HTMLSelectElement)?.value || '';
      if (ampm === 'PM' && hour < 12) date.hour(hour + 12);
      if (ampm === 'AM' && hour === 12) date.hour(0);
    }

    date.hour(hour);
    date.minute(minute);
    date.second(second);

    if (side === 'left') {
      this.setStartDate(date);
      if (this.singleDatePicker) {
        this.endDate = this.startDate.clone();
      } else if (
        this.endDate &&
        this.endDate.format('YYYY-MM-DD') === date.format('YYYY-MM-DD') &&
        this.endDate.isBefore(date)
      ) {
        this.setEndDate(date.clone());
      }
    } else if (this.endDate) {
      this.setEndDate(date);
    }

    this.updateCalendars();
    this.updateFormInputs();
    this.renderTimePicker('left');
    this.renderTimePicker('right');
  }

  private showLeftCalendars(): boolean {
    if (
      this.withSelectionTypes &&
      (this.selectionMode === 'week' ||
        this.selectionMode === 'date' ||
        this.selectionMode === 'month' ||
        this.selectionMode === 'year')
    ) {
      return false;
    } else if (this.withSelectionTypes) {
      this.container.classList.add('ranges');
      return false;
    } else {
      if (this.ranges && Object.keys(this.ranges).length > 0) {
        return true;
      }
      if (
        this.selectionMode === 'week' ||
        this.selectionMode === 'month' ||
        this.selectionMode === 'year'
      ) {
        return false;
      }
      if (this.singleDatePicker) {
        return false;
      }
      return true;
    }
  }
  private show(): void {
    this.oldStartDate = this.startDate.clone();
    this.oldEndDate = this.endDate?.clone();
    this.previousRightTime = this.endDate?.clone() || this.previousRightTime;
    this.updateView();
    if (this.isShowing || this.inline || this.disabled) return;
    this.container.classList.add('show');
    this.container.style.display = 'flex';
    this.isShowing = true;
    if (this.showLeftCalendars()) {
      const calendars = this.container.querySelectorAll('.drp-calendar');
      calendars.forEach((calendar) => {
        (calendar as HTMLElement).style.display = 'flex';
      });
    } else {
      const leftCalendar = this.container.querySelector(
        '.drp-calendar.left',
      ) as HTMLElement;
      const rightCalendar = this.container.querySelector(
        '.drp-calendar.right',
      ) as HTMLElement;
      if (leftCalendar) {
        leftCalendar.style.display = 'flex';
      }
      if (rightCalendar) {
        rightCalendar.style.display = 'none';
      }
    }

    // Position the container
    this.move();

    // Add outside click handler
    document.addEventListener('click', this._outsideClick);
  }

  private hide(): void {
    const windowWidth = window.innerWidth;
    const isMobile = windowWidth < 768;
    if (isMobile) {
      document.removeEventListener('click', this._outsideClick);
      this.container.style.display = 'none';
      this.container.classList.remove('show');
      // this.isShowing = false;
      // return;
    }
    // incomplete date selection, revert to last values
    if (!this.endDate) {
      if (this.oldStartDate) this.startDate = this.oldStartDate.clone();
      if (this.oldEndDate) this.endDate = this.oldEndDate.clone();
    }

    // if a new date range was selected, invoke the user callback function
    if (
      !this.startDate.isSame(this.oldStartDate) ||
      !this.endDate?.isSame(this.oldEndDate)
    ) {
      this.callback(this.startDate, this.endDate, this.chosenLabel);
      this.onChange && this.onChange(this.startDate, this.endDate);
    }

    console.log(' isShowing ', this.isShowing);
    console.log(' inline ', this.inline);

    if (!this.isShowing || this.inline) return;
    // if picker is attached to a text input, update it
    this.updateElement();
    this.container.classList.remove('show');
    document.removeEventListener('click', this._outsideClick);
    this.container.style.display = 'none';
    this.isShowing = false;
  }

  private toggle(): void {
    if (this.isShowing) {
      this.hide();
    } else {
      this.show();
    }
  }

  public updateElement(): void {
    if (this.element.tagName === 'INPUT' && this.autoUpdateInput) {
      let newValue = '';
      if (
        this.singleDatePicker ||
        this.selectionMode === 'month' ||
        this.selectionMode === 'year'
      ) {
        newValue = this.startDate.format(this.locale.format);
      } else {
        newValue =
          this.startDate.format(this.locale.format) +
          this.locale.separator +
          this.endDate?.format(this.locale.format);
      }
      if (newValue !== (this.element as HTMLInputElement).value) {
        (this.element as HTMLInputElement).value = newValue;
        (this.element as HTMLInputElement).dispatchEvent(new Event('change'));
        this.element.classList.add('active');
      }
    }
  }

  private updateMonthsInView(): void {
    if (this.endDate) {
      // if both dates are visible already, do nothing
      if (
        !this.singleDatePicker &&
        this.leftCalendar.month &&
        this.rightCalendar.month &&
        (this.startDate.format('YYYY-MM') ===
          this.leftCalendar.month.format('YYYY-MM') ||
          this.startDate.format('YYYY-MM') ===
            this.rightCalendar.month.format('YYYY-MM')) &&
        (this.endDate.format('YYYY-MM') ===
          this.leftCalendar.month.format('YYYY-MM') ||
          this.endDate.format('YYYY-MM') ===
            this.rightCalendar.month.format('YYYY-MM'))
      ) {
        return;
      }

      this.leftCalendar.month = this.startDate.clone().date(2);
      if (
        !this.linkedCalendars &&
        (this.endDate.month() !== this.startDate.month() ||
          this.endDate.year() !== this.startDate.year())
      ) {
        this.rightCalendar.month = this.endDate.clone().date(2);
      } else {
        this.rightCalendar.month = this.startDate
          .clone()
          .date(2)
          .add(1, 'month');
      }
    } else {
      if (
        this.leftCalendar.month.format('YYYY-MM') !==
          this.startDate.format('YYYY-MM') &&
        this.rightCalendar.month.format('YYYY-MM') !==
          this.startDate.format('YYYY-MM')
      ) {
        this.leftCalendar.month = this.startDate.clone().date(2);
        this.rightCalendar.month = this.startDate
          .clone()
          .date(2)
          .add(1, 'month');
      }
    }
    if (
      this.maxDate &&
      this.linkedCalendars &&
      !this.singleDatePicker &&
      this.rightCalendar.month > this.maxDate
    ) {
      this.rightCalendar.month = this.maxDate.clone().date(2);
      this.leftCalendar.month = this.maxDate
        .clone()
        .date(2)
        .subtract(1, 'month');
    }
  }

  public updateCalendars(): void {
    if (this.timePicker) {
      let hour, minute, second;
      if (this.endDate) {
        hour = parseInt(
          (
            this.container.querySelector(
              '.right .hourselect',
            ) as HTMLSelectElement
          )?.value || '0',
          10,
        );
        minute = parseInt(
          (
            this.container.querySelector(
              '.right .minuteselect',
            ) as HTMLSelectElement
          )?.value || '0',
          10,
        );
        if (isNaN(minute)) {
          minute = parseInt(
            (
              this.container.querySelector(
                '.right .minuteselect option:last-child',
              ) as HTMLSelectElement
            )?.value || '0',
            10,
          );
        }
        second = this.timePickerSeconds
          ? parseInt(
              (
                this.container.querySelector(
                  '.right .secondselect',
                ) as HTMLSelectElement
              )?.value || '0',
              10,
            )
          : 0;
        if (!this.timePicker24Hour) {
          const ampm =
            (
              this.container.querySelector(
                '.right .ampmselect',
              ) as HTMLSelectElement
            )?.value || '';
          if (ampm === 'PM' && hour < 12) hour += 12;
          if (ampm === 'AM' && hour === 12) hour = 0;
        }
      }
    }
    this.renderCalendar('left');
    if (!this.singleDatePicker) this.renderCalendar('right');

    // highlight any predefined range matching the current start and end dates
    // this.container.querySelectorAll('.ranges li').forEach((range) => {
    //   range.classList.remove('active');
    // });
    if (this.endDate == null) return;

    this.calculateChosenLabel();
  }

  private calculateChosenLabel(): void {
    let customRange = true;
    const rangesEl = this.container.querySelector('.ranges');
    if (rangesEl) {
      const ranges = rangesEl.querySelectorAll('li');
      // for (const range of Array.from(ranges)) {
      //   if (range.textContent === this.chosenLabel) {
      //     customRange = false;
      //     range.classList.add('active');
      //     break;
      //   }
      // }
      if (customRange) {
        // if (this.showCustomRangeLabel) {
        //   ranges[ranges.length - 1]?.classList.add('active');
        // }
        this.chosenLabel = this.showCustomRangeLabel
          ? this.locale.customRangeLabel
          : '';
      }
    }
  }

  private clickPrev(e: Event): void {
    if (this.selectionMode === 'year' || this.selectionMode === 'month') {
      this.leftCalendar.month = this.leftCalendar.month
        .clone()
        .subtract(1, 'year');
      if (this.selectionMode === 'year') {
        this.setStartDate(this.leftCalendar.month.clone().startOf('year'));
      }
      this.updateCalendars();
      this.updateFormInputs();
      return;
    }
    const target = e.target as HTMLElement;
    const cal = target.closest('.drp-calendar');
    if (!cal) return;

    const isLeft = cal.classList.contains('left');

    // Store current selected dates
    const oldStartDate = this.startDate.clone();
    const oldEndDate = this.endDate?.clone();

    if (isLeft) {
      // Update left calendar view
      this.leftCalendar.month = this.leftCalendar.month
        .clone()
        .subtract(1, 'month');

      if (this.linkedCalendars) {
        this.rightCalendar.month = this.rightCalendar.month
          .clone()
          .subtract(1, 'month');
      }
    } else {
      // Update right calendar view
      this.rightCalendar.month = this.rightCalendar.month
        .clone()
        .subtract(1, 'month');
    }

    // Check if new month view is within allowed range
    if (
      this.minDate &&
      this.leftCalendar.month.isBefore(this.minDate, 'month')
    ) {
      this.leftCalendar.month = this.minDate.clone();
      if (this.linkedCalendars) {
        this.rightCalendar.month = this.minDate.clone().add(1, 'month');
      }
    }

    // Restore selected dates
    this.startDate = oldStartDate;
    if (oldEndDate) {
      this.endDate = oldEndDate;
    }

    this.updateCalendars();
    this.updateFormInputs();
  }

  private clickNext(e: Event): void {
    if (this.selectionMode === 'year' || this.selectionMode === 'month') {
      this.leftCalendar.month = this.leftCalendar.month.clone().add(1, 'year');
      if (this.selectionMode === 'year') {
        this.setStartDate(this.leftCalendar.month.clone().startOf('year'));
      }
      this.updateCalendars();
      this.updateFormInputs();
      return;
    }

    const target = e.target as HTMLElement;
    const cal = target.closest('.drp-calendar');
    if (!cal) return;

    const isLeft = cal.classList.contains('left');

    // Store current selected dates
    const oldStartDate = this.startDate.clone();
    const oldEndDate = this.endDate?.clone();

    if (isLeft) {
      // Update left calendar view
      this.leftCalendar.month = this.leftCalendar.month.clone().add(1, 'month');

      if (this.linkedCalendars) {
        this.rightCalendar.month = this.rightCalendar.month
          .clone()
          .add(1, 'month');
      }
    } else {
      // Update right calendar view
      this.rightCalendar.month = this.rightCalendar.month
        .clone()
        .add(1, 'month');

      // Update left calendar to be one month behind the right calendar
      this.leftCalendar.month = this.rightCalendar.month
        .clone()
        .subtract(1, 'month');
    }

    // Check if new month view is within allowed range
    if (
      this.maxDate &&
      this.rightCalendar.month.isAfter(this.maxDate, 'month')
    ) {
      this.rightCalendar.month = this.maxDate.clone();
      this.leftCalendar.month = this.maxDate.clone().subtract(1, 'month');
    }

    // Restore selected dates
    this.startDate = oldStartDate;
    if (oldEndDate) {
      this.endDate = oldEndDate;
    }

    this.updateCalendars();
    this.updateFormInputs();
  }

  private hoverDate(e: Event): void {
    const target = e.target as HTMLElement;
    const title = target.getAttribute('data-title');

    if (title) {
      const row = parseInt(title.substr(1, 1));
      const col = parseInt(title.substr(3, 1));
      const cal = target.closest('.drp-calendar');
      if (cal) {
        const side = cal.classList.contains('left') ? 'left' : 'right';
        const calendar =
          side === 'left' ? this.leftCalendar : this.rightCalendar;

        if (calendar.calendar) {
          const date = calendar.calendar[row][col];
          this.onHoverDateChange(date);
        }
      }
    }

    // Only show range preview if we have a start date selected but no end date
    if (this.startDate && !this.endDate) {
      // Remove any existing in-range classes
      const cells = this.container.querySelectorAll(
        '.drp-calendar tbody td.available',
      );
      cells.forEach((cell) => {
        cell.classList.remove('in-range');
      });

      // Find the start cell
      const startCell = Array.from(cells).find((cell) =>
        cell.classList.contains('start-date'),
      );

      if (!startCell) return;

      const startIndex = Array.from(cells).indexOf(startCell);
      const hoverIndex = Array.from(cells).indexOf(target);

      if (startIndex === -1 || hoverIndex === -1) return;

      // Add in-range class to cells between start and hover, excluding "off" cells
      const start = Math.min(startIndex, hoverIndex);
      const end = Math.max(startIndex, hoverIndex);

      for (let i = start + 1; i < end; i++) {
        const cell = cells[i] as HTMLElement;
        // FIX: Only add in-range class if the cell doesn't have "off" class
        if (
          cell &&
          !cell.classList.contains('off') &&
          !cell.classList.contains('disabled')
        ) {
          cell.classList.add('in-range');
        }
      }
    }
  }

  private updateFormInputs(): void {
    if (this.container.querySelector('.drp-header span.drp-selected')) {
      let dateString = this.startDate.format(this.locale.format);
      if (!this.singleDatePicker) {
        dateString +=
          this.locale.separator +
          (this.endDate ? this.endDate.format(this.locale.format) : '');
      }
      (
        this.container.querySelector(
          '.drp-header span.drp-selected',
        ) as HTMLElement
      ).textContent = dateString;
    }

    if (this.singleDatePicker && this.endDate) {
      (
        this.container.querySelector('.drp-calendar.right') as HTMLElement
      ).style.display = 'none';
    }
  }

  private elementChanged(): void {
    if (!(this.element instanceof HTMLInputElement)) return;
    if (!this.element.value.length) return;

    const dateString = this.element.value.split(this.locale.separator);
    let start = null;
    let end = null;

    if (dateString.length === 2) {
      start = moment(dateString[0], this.locale.format);
      end = moment(dateString[1], this.locale.format);
    } else if (this.singleDatePicker && dateString.length === 1) {
      start = moment(dateString[0], this.locale.format);
      end = start.clone();
    }

    if (start !== null && end !== null) {
      if (end.isBefore(start)) end = start.clone();

      this.setStartDate(start);
      this.setEndDate(end);
      this.updateView();
    }
  }

  private keydown(e: KeyboardEvent): void {
    // hide on tab or enter
    if (e.keyCode === 9 || e.keyCode === 13) {
      this.hide();
    }
  }

  public updateView(): void {
    if (this.timePicker) {
      this.renderTimePicker('left');
      this.renderTimePicker('right');
    }
    this.updateMonthsInView();
    this.updateCalendars();
    this.updateFormInputs();
  }

  public setStartDate(startDate: moment.Moment): void {
    if (typeof startDate === 'string')
      this.startDate = moment(startDate, this.locale.format);

    if (typeof startDate === 'object') this.startDate = moment(startDate);

    if (!this.timePicker) this.startDate = this.startDate.startOf('day');

    if (this.timePicker && this.timePickerIncrement)
      this.startDate.minute(
        Math.round(this.startDate.minute() / this.timePickerIncrement) *
          this.timePickerIncrement,
      );

    if (this.minDate && this.startDate.isBefore(this.minDate)) {
      this.startDate = this.minDate.clone();
      if (this.timePicker && this.timePickerIncrement)
        this.startDate.minute(
          Math.round(this.startDate.minute() / this.timePickerIncrement) *
            this.timePickerIncrement,
        );
    }

    if (this.maxDate && this.startDate.isAfter(this.maxDate)) {
      this.startDate = this.maxDate.clone();
      if (this.timePicker && this.timePickerIncrement)
        this.startDate.minute(
          Math.floor(this.startDate.minute() / this.timePickerIncrement) *
            this.timePickerIncrement,
        );
    }

    if (!this.isShowing) this.updateElement();

    this.updateMonthsInView();
  }

  public setEndDate(endDate: moment.Moment): void {
    if (typeof endDate === 'string')
      this.endDate = moment(endDate, this.locale.format);

    if (typeof endDate === 'object') this.endDate = moment(endDate);

    if (!this.timePicker) this.endDate = this.endDate.endOf('day');

    if (this.timePicker && this.timePickerIncrement)
      this.endDate.minute(
        Math.round(this.endDate.minute() / this.timePickerIncrement) *
          this.timePickerIncrement,
      );

    if (this.endDate.isBefore(this.startDate))
      this.endDate = this.startDate.clone();

    if (this.maxDate && this.endDate.isAfter(this.maxDate))
      this.endDate = this.maxDate.clone();

    if (
      this.maxSpan &&
      this.startDate.clone().add(this.maxSpan).isBefore(this.endDate)
    )
      this.endDate = this.startDate.clone().add(this.maxSpan);

    this.previousRightTime = this.endDate.clone();

    if (!this.isShowing) this.updateElement();

    this.updateMonthsInView();
  }

  public isInvalidDate(date: moment.Moment): boolean {
    return false;
  }

  private isCustomDate(date: moment.Moment): boolean | string | string[] {
    return false;
  }

  public remove(): void {
    document.removeEventListener('click', this._outsideClick);
    window.removeEventListener('resize', this._handleResizeProxy);
    this.container.remove();
    this.element.removeEventListener('click', () => this.show());
    this.element.removeEventListener('focus', () => this.show());
    this.element.removeEventListener('keyup', () => this.elementChanged());
    this.element.removeEventListener('keydown', (e) => this.keydown(e));
  }

  private renderTimePicker(side: 'left' | 'right'): void {
    if (!this.timePicker) {
      return;
    }

    const selected = side === 'left' ? this.startDate : this.endDate;
    const calendar = side === 'left' ? this.leftCalendar : this.rightCalendar;

    if (!selected) {
      return;
    }

    let html = '<select class="hourselect">';
    const start = this.timePicker24Hour ? 0 : 1;
    const end = this.timePicker24Hour ? 23 : 12;

    for (let i = start; i <= end; i++) {
      const i_in_24 = this.timePicker24Hour
        ? i
        : selected.hour() >= 12
          ? i === 12
            ? 12
            : i + 12
          : i === 12
            ? 0
            : i;
      const time = selected.clone().hour(i_in_24);
      let disabled = false;

      if (this.minDate && time.minute(59).isBefore(this.minDate)) {
        disabled = true;
      }
      if (this.maxDate && time.minute(0).isAfter(this.maxDate)) {
        disabled = true;
      }

      if (i_in_24 === selected.hour() && !disabled) {
        html += `<option value="${i}" selected="selected">${i}</option>`;
      } else if (disabled) {
        html += `<option value="${i}" disabled="disabled" class="disabled">${i}</option>`;
      } else {
        html += `<option value="${i}">${i}</option>`;
      }
    }

    html += '</select> : ';

    html += '<select class="minuteselect">';

    for (let i = 0; i < 60; i += this.timePickerIncrement) {
      const padded = i < 10 ? '0' + i : i;
      const time = selected.clone().minute(i);
      let disabled = false;

      if (this.minDate && time.second(59).isBefore(this.minDate)) {
        disabled = true;
      }
      if (this.maxDate && time.second(0).isAfter(this.maxDate)) {
        disabled = true;
      }

      if (selected.minute() === i && !disabled) {
        html += `<option value="${i}" selected="selected">${padded}</option>`;
      } else if (disabled) {
        html += `<option value="${i}" disabled="disabled" class="disabled">${padded}</option>`;
      } else {
        html += `<option value="${i}">${padded}</option>`;
      }
    }

    html += '</select> ';

    if (this.timePickerSeconds) {
      html += ': <select class="secondselect">';

      for (let i = 0; i < 60; i++) {
        const padded = i < 10 ? '0' + i : i;
        const time = selected.clone().second(i);
        let disabled = false;

        if (this.minDate && time.isBefore(this.minDate)) {
          disabled = true;
        }
        if (this.maxDate && time.isAfter(this.maxDate)) {
          disabled = true;
        }

        if (selected.second() === i && !disabled) {
          html += `<option value="${i}" selected="selected">${padded}</option>`;
        } else if (disabled) {
          html += `<option value="${i}" disabled="disabled" class="disabled">${padded}</option>`;
        } else {
          html += `<option value="${i}">${padded}</option>`;
        }
      }

      html += '</select> ';
    }

    if (!this.timePicker24Hour) {
      html += '<select class="ampmselect">';
      const am_html =
        this.minDate &&
        selected.clone().hour(12).minute(0).second(0).isBefore(this.minDate)
          ? ' disabled="disabled" class="disabled"'
          : '';
      const pm_html =
        this.maxDate &&
        selected.clone().hour(0).minute(0).second(0).isAfter(this.maxDate)
          ? ' disabled="disabled" class="disabled"'
          : '';

      if (selected.hour() >= 12) {
        html += `<option value="AM"${am_html}>AM</option><option value="PM" selected="selected"${pm_html}>PM</option>`;
      } else {
        html += `<option value="AM" selected="selected"${am_html}>AM</option><option value="PM"${pm_html}>PM</option>`;
      }

      html += '</select>';
    }

    const timePickerEl = this.container.querySelector(
      `.drp-calendar.${side} .calendar-time`,
    );
    if (timePickerEl) {
      timePickerEl.innerHTML = html;
    }
  }

  private renderCustomSelect(
    options: {
      value: string | number;
      label: string;
      selected: boolean;
      disabled?: boolean;
    }[],
    className: string,
    selectedValue: string | number,
  ): string {
    const selectedOption = options.find((opt) => opt.selected) || options[0];
    const selectedLabel = selectedOption ? selectedOption.label : '';

    let html = `<div class="minthr-custom-select-container ${className}" data-value="${selectedValue}">`;
    html += `<div class="minthr-custom-select-trigger">`;
    html += `<span class="minthr-custom-select-text">${selectedLabel}</span>`;
    html += `<span class="minthr-custom-select-arrow"></span>`;
    html += `</div>`;
    html += `<div class="minthr-custom-options ${className}">`;

    options.forEach((option) => {
      const isSelected = option.selected ? 'selected' : '';
      const isDisabled = option.disabled ? 'disabled' : '';
      const classes = `minthr-custom-option ${isSelected} ${isDisabled}`.trim();
      if (!option.disabled) {
        html += `<div class="${classes}" data-value="${option.value}">${option.label}</div>`;
      }
    });

    html += `</div>`;
    html += `</div>`;

    return html;
  }

  private renderCalendar(side: 'left' | 'right'): void {
    const calendar = side === 'left' ? this.leftCalendar : this.rightCalendar;
    if (this.DayRangePicker) {
      calendar.month = moment('2025-10-01');
    }
    const month = calendar.month.month();
    const year = calendar.month.year();
    const hour = calendar.month.hour();
    const minute = calendar.month.minute();
    const second = calendar.month.second();
    const daysInMonth = moment([year, month]).daysInMonth();
    const firstDay = moment([year, month, 1]);
    const lastDay = moment([year, month, daysInMonth]);
    const dayOfWeek = firstDay.day();

    const calendarMatrix: moment.Moment[][] = [];

    if (this.DayRangePicker) {
      const daysPerRow = 8;
      const numRows = Math.ceil(daysInMonth / daysPerRow);

      for (let i = 0; i < numRows; i++) {
        calendarMatrix[i] = [];
      }

      let row = 0;
      let col = 0;

      for (let day = 1; day <= daysInMonth; day++) {
        if (col === daysPerRow) {
          col = 0;
          row++;
        }

        const curDate = moment([year, month, day, hour, minute, second]);
        calendarMatrix[row][col] = curDate.clone();
        col++;
      }

      // Fill remaining cells in the last row with null
      while (col < daysPerRow && col > 0) {
        calendarMatrix[row][col] = null;
        col++;
      }
    } else {
      const lastMonth = moment(firstDay).subtract(1, 'month').month();
      const lastYear = moment(firstDay).subtract(1, 'month').year();
      const daysInLastMonth = moment([lastYear, lastMonth]).daysInMonth();

      for (let i = 0; i < 6; i++) {
        calendarMatrix[i] = [];
      }

      // Calculate start day for the calendar grid
      let startDay = daysInLastMonth - dayOfWeek + this.locale.firstDay + 1;
      if (startDay > daysInLastMonth) startDay -= 7;

      if (dayOfWeek === this.locale.firstDay) startDay = daysInLastMonth - 6;

      // Start with last month's days
      let curDate = moment([lastYear, lastMonth, startDay, 12, minute, second]);
      let nextMonth = false;
      let row = 0;
      let col = 0;
      const daysPerRow = this.DayRangePicker ? 8 : 7;
      for (let i = 0; i < 42; i++) {
        if (i > 0 && col % daysPerRow === 0) {
          col = 0;
          row++;
        }

        // Clone the date to avoid mutations
        calendarMatrix[row][col] = curDate.clone();

        // Check if we've moved to the next month
        if (curDate.month() > month && !nextMonth) {
          nextMonth = true;
        }

        // Increment the date
        curDate.add(1, 'day');
        col++;
      }
    }

    // Store the calendar matrix
    if (side === 'left') {
      this.leftCalendar.calendar = calendarMatrix;
    } else {
      this.rightCalendar.calendar = calendarMatrix;
    }

    // Render the calendar days
    this.renderCalendarDays(side, calendarMatrix);
  }
  private renderRanges(): void {
    const rangesEl = this.container.querySelector('.ranges');
    const ul = document.createElement('ul');
    if (rangesEl) {
      rangesEl.classList.add('show-ranges');
      Object.entries(this.ranges).forEach(([key, range]) => {
        const rangeEl = document.createElement('li');
        rangeEl.setAttribute('data-range-key', key);
        rangeEl.innerHTML = key;
        ul.appendChild(rangeEl);
      });
      const customRangeEl = document.createElement('li');
      customRangeEl.setAttribute('data-range-key', 'custom-range');
      customRangeEl.classList.add('active');
      customRangeEl.classList.add('custom-range');
      customRangeEl.innerHTML = 'Custom';
      ul.appendChild(customRangeEl);
      rangesEl.appendChild(ul);
    }
  }
  private renderSelectionTypes(): void {
    const selectionTypesEl = this.container.querySelector('.ranges');
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      const list = this.renderCustomSelect(
        this.selectionTypes.map((type) => ({
          value: type.value,
          label: type.label,
          selected: type.value === this.selectionMode,
        })),
        'rangeselect',
        this.selectionMode,
      );
      if (selectionTypesEl) {
        selectionTypesEl.innerHTML = list;
      }
      return;
    }
    const ul = document.createElement('ul');
    if (selectionTypesEl) {
      selectionTypesEl.classList.add('show-ranges');
      this.selectionTypes.forEach((type) => {
        const typeEl = document.createElement('li');
        typeEl.setAttribute('data-selection-type', type.value);
        if (this.selectionMode === type.value) {
          typeEl.classList.add('active');
        }
        typeEl.innerHTML = this.t(type.label);
        ul.appendChild(typeEl);
      });
      selectionTypesEl.appendChild(ul);
    }
  }

  private renderCalendarDays(
    side: 'left' | 'right',
    calendarMatrix: moment.Moment[][],
  ): void {
    const calendar = side === 'left' ? this.leftCalendar : this.rightCalendar;
    const minDate = side === 'left' ? this.minDate : this.startDate;
    const maxDate = this.maxDate;
    const selected = side === 'left' ? this.startDate : this.endDate;

    let html = '<table class="table-condensed">';
    html += '<thead>';
    html += '<tr>';

    // add empty cell for week number
    if (this.showWeekNumbers || this.showISOWeekNumbers) html += '<th></th>';

    if (
      (!minDate || minDate.isBefore(calendar.month)) &&
      (!this.linkedCalendars || side === 'left')
    ) {
      html += '<th class="prev available"><span></span></th>';
    } else {
      html += '<th></th>';
    }

    let dateHtml =
      this.locale.monthNames[calendar.month.month()] +
      calendar.month.format(' YYYY');

    if (this.showDropdowns) {
      const currentMonth = calendar.month.month();
      const currentYear = calendar.month.year();
      const maxYear = (maxDate && maxDate.year()) || this.maxYear;
      const minYear = (minDate && minDate.year()) || this.minYear;
      const inMinYear = currentYear === Number(minYear);
      const inMaxYear = currentYear === Number(maxYear);

      let monthHtml = '';
      if (this.selectionMode !== 'month' && this.selectionMode !== 'year') {
        const monthOptions = [];
        for (let m = 0; m < 12; m++) {
          if (
            (!inMinYear || (minDate && m >= minDate.month())) &&
            (!inMaxYear || (maxDate && m <= maxDate.month()))
          ) {
            monthOptions.push({
              value: m,
              label: this.locale.monthNames[m],
              selected: m === currentMonth,
              disabled: false,
            });
          } else {
            monthOptions.push({
              value: m,
              label: this.locale.monthNames[m],
              selected: m === currentMonth,
              disabled: true,
            });
          }
        }
        monthHtml = this.renderCustomSelect(
          monthOptions,
          'monthselect',
          currentMonth,
        );
      }

      const yearOptions = [];
      for (let y = Number(minYear); y <= Number(maxYear); y++) {
        yearOptions.push({
          value: y,
          label: y.toString(),
          selected: y === currentYear,
          disabled: false,
        });
      }
      const yearHtml = this.renderCustomSelect(
        yearOptions,
        'yearselect',
        currentYear,
      );

      dateHtml =
        this.selectionMode !== 'month' && this.selectionMode !== 'year'
          ? monthHtml + yearHtml
          : yearHtml;
    }

    html += `<th colspan="5"  class="month ${this.selectionMode == 'month' || this.selectionMode == 'year' ? 'picker-month' : ''}">${dateHtml}</th>`;
    if (
      ((!maxDate || maxDate.isAfter(calendar.month)) &&
        (!this.linkedCalendars || side === 'right' || this.singleDatePicker)) ||
      this.selectionMode === 'month' ||
      this.selectionMode === 'year' ||
      this.selectionMode === 'week' ||
      window.innerWidth < 768
    ) {
      html += '<th class="next available"><span></span></th>';
    } else {
      html += '<th></th>';
    }

    html += '</tr>';
    html += '<tr>';

    // add week number label
    if (this.showWeekNumbers || this.showISOWeekNumbers) {
      html += `<th class="week">${this.locale.weekLabel}</th>`;
    }
    const daysPerRow = this.DayRangePicker ? 8 : 7;

    if (this.selectionMode === 'month') {
      const currentMonth = calendar.month.month();
      html += ` <tr> <td colspan=${daysPerRow} class="w-100"> <div class="minthr-d-flex minthr-flex-wrap minthr-mt-2 container-months "> `;
      for (let m = 0; m < 12; m++) {
        html += `<div class="available available-month ${currentMonth == m ? 'active' : ''}"  data-month="${m}" > ${this.locale.monthNames[m]} </div> `;
      }
      html += '</div></td></tr>';
    } else if (this.selectionMode === 'year') {
      const maxYear = (maxDate && maxDate.year()) || this.maxYear;
      const minYear = (minDate && minDate.year()) || this.minYear;
      const ListYears = Array.from(
        { length: Number(maxYear) - Number(minYear) + 1 },
        (_, i) => Number(minYear) + i,
      );
      const currentYear = calendar.month.year();
      const startYear = currentYear - Math.floor(currentYear % 10);

      const index = ListYears.findIndex((year) => year === startYear);
      html += `<tr > <td colspan=${daysPerRow} class="w-100"> <div class="minthr-d-flex minthr-flex-wrap minthr-mt-2 container-years "> `;

      ListYears.slice(index, index + 12).forEach((year) => {
        html += `<div class="available available-year ${this.startDate && this.startDate.year() == year ? 'active' : ''}"  data-year="${year}" > ${year} </div> `;
      });
      html += '</div></td></tr>';
    } else {
      console.log('this.locale.daysOfWeek', this.locale.daysOfWeek);

      this.locale.daysOfWeek.forEach((dayOfWeek) => {
        html += `<th>${dayOfWeek}</th>`;
      });
    }

    html += '</tr>';
    html += '</thead>';
    html += '<tbody>';

    //adjust maxDate to reflect the maxSpan setting in order to
    //grey out end dates beyond the maxSpan
    let adjustedMaxDate = maxDate;
    if (this.endDate === null && this.maxSpan && this.startDate) {
      const maxLimit = this.startDate.clone().add(this.maxSpan).endOf('day');
      if (!maxDate || maxLimit.isBefore(maxDate)) {
        adjustedMaxDate = maxLimit;
      }
    }

    if (this.selectionMode === 'date' || this.selectionMode === 'week') {
      // if (this.selectionMode === 'week') {
      //   const startOfWeek = this.startDate.clone().startOf('week');
      //   const endOfWeek = this.startDate.clone().endOf('week');
      //   this.setStartDate(startOfWeek);
      //   this.setEndDate(endOfWeek);
      // }
      for (let row = 0; row < 6; row++) {
        html += `<tr ${this.selectionMode === 'week' ? 'class="week"' : ''}>`;

        // add week number
        if (this.showWeekNumbers) {
          if (calendarMatrix[row][0] !== null) {
            html += `<td class="week">${calendarMatrix[row][0].week()}</td>`;
          } else {
            html += '<td class="week"></td>';
          }
        } else if (this.showISOWeekNumbers) {
          if (calendarMatrix[row][0] !== null) {
            html += `<td class="week">${calendarMatrix[row][0].isoWeek()}</td>`;
          } else {
            html += '<td class="week"></td>';
          }
        }

        for (let col = 0; col < daysPerRow; col++) {
          // Skip if row or column doesn't exist or is null
          if (!calendarMatrix[row] || !calendarMatrix[row][col]) {
            continue;
          }
          const classes: string[] = [];

          //highlight today's date
          if (calendarMatrix[row][col].isSame(new Date(), 'day')) {
            classes.push('today');
          }

          //highlight weekends
          if (calendarMatrix[row][col].isoWeekday() > 5) {
            classes.push('weekend');
          }

          //grey out the dates in other months displayed at beginning and end of this calendar
          if (calendarMatrix[row][col].month() !== calendar.month.month()) {
            classes.push('off', 'ends');
          }

          //don't allow selection of dates before the minimum date
          if (
            this.minDate &&
            calendarMatrix[row][col].isBefore(this.minDate, 'day')
          ) {
            classes.push('off', 'disabled');
          }

          //don't allow selection of dates after the maximum date
          if (
            adjustedMaxDate &&
            calendarMatrix[row][col].isAfter(adjustedMaxDate, 'day')
          ) {
            classes.push('off', 'disabled');
          }

          //don't allow selection of date if a custom function decides it's invalid
          if (
            this.isInvalidDate &&
            this.isInvalidDate(calendarMatrix[row][col])
          ) {
            classes.push('off', 'disabled');
          } else {
            classes.push('available');
          }

          //highlight the currently selected start date
          if (
            this.startDate &&
            calendarMatrix[row][col].format('YYYY-MM-DD') ===
              this.startDate.format('YYYY-MM-DD') &&
            calendarMatrix[row][col].month() === calendar.month.month()
          ) {
            classes.push('active', 'start-date');
          }

          //highlight the currently selected end date
          if (
            this.endDate !== null &&
            calendarMatrix[row][col].format('YYYY-MM-DD') ===
              this.endDate.format('YYYY-MM-DD') &&
            calendarMatrix[row][col].month() === calendar.month.month()
          ) {
            classes.push('active', 'end-date');
          }

          //highlight dates in-between the selected dates
          if (
            this.endDate !== null &&
            this.startDate &&
            calendarMatrix[row][col].isAfter(this.startDate, 'day') &&
            calendarMatrix[row][col].isBefore(this.endDate, 'day') &&
            calendarMatrix[row][col].month() === calendar.month.month()
          ) {
            classes.push('in-range');
          }

          // highlight preview range
          if (
            this.hoverStartDate &&
            this.hoverEndDate &&
            calendarMatrix[row][col].isSameOrAfter(
              this.hoverStartDate,
              'day',
            ) &&
            calendarMatrix[row][col].isSameOrBefore(this.hoverEndDate, 'day') &&
            calendarMatrix[row][col].month() === calendar.month.month()
          ) {
            classes.push('preview-range');
            if (calendarMatrix[row][col].isSame(this.hoverStartDate, 'day')) {
              classes.push('start-date');
            }
            if (calendarMatrix[row][col].isSame(this.hoverEndDate, 'day')) {
              classes.push('end-date');
            }
          }

          //apply custom classes for this date
          const isCustom = this.isCustomDate(calendarMatrix[row][col]);
          if (isCustom !== false) {
            if (typeof isCustom === 'string') {
              classes.push(isCustom);
            } else if (Array.isArray(isCustom)) {
              classes.push(...isCustom);
            }
          }

          const className = classes.length ? classes.join(' ') : 'available';
          html += `<td class="${className}" data-title="r${row}c${col}">${calendarMatrix[row][col].date()}</td>`;
        }
        html += '</tr>';
      }
    }
    html += '</tbody>';
    html += '</table>';

    const calendarEl = this.container.querySelector(
      `.drp-calendar.${side} .calendar-table`,
    );

    if (calendarEl) {
      calendarEl.innerHTML = html;
    }
  }

  private clickRange(e: Event): void {
    const prevActiveRange = this.container.querySelector('.ranges li.active');
    if (prevActiveRange) {
      prevActiveRange.classList.remove('active');
    }
    if (this.withSelectionTypes) {
      const target = e.target as HTMLElement;
      target.classList.add('active');
      const label = target.getAttribute('data-selection-type');

      switch (label) {
        case 'month':
          this.selectionMode = 'month';
          this.singleDatePicker = false;
          break;
        case 'year':
          this.selectionMode = 'year';
          this.singleDatePicker = false;
          break;
        case 'date':
          this.setEndDate(this.startDate);
          this.selectionMode = 'date';
          this.singleDatePicker = true;

          break;
        case 'range':
          this.selectionMode = 'date';
          this.singleDatePicker = false;
          break;
        case 'week':
          this.selectionMode = 'week';
          this.singleDatePicker = false;
          this.linkedCalendars = false;
          break;
      }
      this.updateView();
      return;
    }
    const target = e.target as HTMLElement;
    const label = target.getAttribute('data-range-key');
    if (!label) return;

    const range = this.ranges[label];
    if (range) {
      this.startDate = range[0];
      this.endDate = range[1];

      if (this.showRangeInputs) {
        this.updateElement();
      }

      target.classList.add('active');
      this.updateView();

      if (this.autoApply) {
        this.clickApply(e);
      }
    }
  }
  private clickRangeMobile(e: Event): void {
    const prevActiveRange = this.container.querySelector(
      '.minthr-custom-options .minthr-custom-option.selected',
    );
    if (prevActiveRange) {
      prevActiveRange.classList.remove('selected');
    }
    if (this.withSelectionTypes) {
      const target = e.target as HTMLElement;
      target.classList.add('selected');
      const label = target.getAttribute('data-value');

      switch (label) {
        case 'month':
          this.selectionMode = 'month';
          this.singleDatePicker = false;
          break;
        case 'year':
          this.selectionMode = 'year';
          this.singleDatePicker = false;
          break;
        case 'date':
          this.setEndDate(this.startDate);
          this.selectionMode = 'date';
          this.singleDatePicker = true;

          break;
        case 'range':
          this.selectionMode = 'date';
          this.singleDatePicker = false;
          break;
        case 'week':
          this.selectionMode = 'week';
          this.singleDatePicker = false;
          this.linkedCalendars = false;
          break;
      }
      this.updateView();
      return;
    }
    const target = e.target as HTMLElement;
    const label = target.getAttribute('data-range-key');
    if (!label) return;

    const range = this.ranges[label];
    if (range) {
      this.startDate = range[0];
      this.endDate = range[1];

      if (this.showRangeInputs) {
        this.updateElement();
      }

      target.classList.add('active');
      this.updateView();

      if (this.autoApply) {
        this.clickApply(e);
      }
    }
  }

  private showCalendars(): void {
    this.container.classList.add('show-calendar');
    this.container.classList.remove('show-ranges');
  }

  private hideCalendars(): void {
    this.container.classList.remove('show-calendar');
    this.container.classList.add('show-ranges');
  }

  public getStartDate(): moment.Moment {
    return this.startDate;
  }

  public getEndDate(): moment.Moment | null {
    return this.endDate;
  }

  public setMinDate(minDate: moment.Moment | string | null): void {
    if (minDate === null) {
      this.minDate = null;
    } else {
      this.minDate =
        typeof minDate === 'string'
          ? moment(minDate, this.locale.format)
          : moment(minDate);
      if (!this.timePicker) {
        this.minDate = this.minDate.startOf('day');
      }
    }
    this.updateView();
  }

  public setMaxDate(maxDate: moment.Moment | string | null): void {
    if (maxDate === null) {
      this.maxDate = null;
    } else {
      this.maxDate =
        typeof maxDate === 'string'
          ? moment(maxDate, this.locale.format)
          : moment(maxDate);
      if (!this.timePicker) {
        this.maxDate = this.maxDate.endOf('day');
      }
    }
    this.updateView();
  }

  public setDateRange(
    start: moment.Moment | string,
    end: moment.Moment | string,
  ): void {
    const startDate =
      typeof start === 'string'
        ? moment(start, this.locale.format)
        : moment(start);
    const endDate =
      typeof end === 'string' ? moment(end, this.locale.format) : moment(end);

    this.setStartDate(startDate);
    this.setEndDate(endDate);
    this.updateView();
  }

  public clear(): void {
    this.startDate = moment().startOf('day');
    this.endDate = moment().endOf('day');
    this.updateView();
  }

  private move(): void {
    const parentOffset = { top: 0, left: 0 };
    const drops = this.drops;
    const GAP = 9;

    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const elementRect = this.element.getBoundingClientRect();
    const containerRect = this.container.getBoundingClientRect();
    const isRTL = this.locale.direction === 'rtl';

    const isMobile = windowWidth < 768; // Assuming mobile breakpoint is 768px

    if (this.parentEl !== document.body) {
      const parentRect = (this.parentEl as HTMLElement).getBoundingClientRect();
      parentOffset.top =
        parentRect.top - (this.parentEl as HTMLElement).scrollTop;
      parentOffset.left =
        parentRect.left - (this.parentEl as HTMLElement).scrollLeft;
    }

    if (isMobile) {
      // Center the container on mobile
      const containerTopMobile = (windowHeight - containerRect.height) / 2;
      this.container.style.left = '50%';
      this.container.style.right = 'auto';
      this.container.style.transform = 'translateX(-50%)';
      this.container.style.position = 'fixed';
      document.onscroll = () => {
        this._outsideClick(new Event('click'));
      };
      const leftCalendar = this.container.querySelector(
        '.drp-calendar.left',
      ) as HTMLElement;
      if (leftCalendar && this.selectionMode === 'date') {
        leftCalendar.style.marginRight = 'calc(var(--unit-minthr) * 20)';
      }
      this.container.style.top = `${containerTopMobile}px`;
      return;
    }

    // Reset positioning so we can measure the container at its natural size
    this.container.style.top = '0';
    this.container.style.left = '0';
    this.container.style.right = 'auto';
    this.container.style.transform = 'none';

    const containerWidth = this.container.offsetWidth;
    const containerHeight = this.container.offsetHeight;

    const spaceBelow = windowHeight - elementRect.bottom;
    const spaceAbove = elementRect.top;
    const fitsBelow = containerHeight + GAP <= spaceBelow;
    const fitsAbove = containerHeight + GAP <= spaceAbove;

    // Resolve vertical placement: bottom first, then top, then side fallback.
    let placement: 'down' | 'up' | 'side';
    if (drops === 'up') {
      placement = fitsAbove ? 'up' : fitsBelow ? 'down' : 'side';
    } else if (drops === 'down') {
      placement = fitsBelow ? 'down' : fitsAbove ? 'up' : 'side';
    } else {
      placement = fitsBelow ? 'down' : fitsAbove ? 'up' : 'side';
    }

    this.container.classList.toggle('drop-up', placement === 'up');
    this.container.classList.toggle('drop-side', placement === 'side');

    if (placement === 'side') {
      // Neither top nor bottom fits: open as a side flyout.
      // LTR -> open on the LEFT of the trigger, RTL -> open on the RIGHT.
      let containerLeft: number;
      if (isRTL) {
        containerLeft = elementRect.right + GAP - parentOffset.left;
        if (containerLeft + containerWidth > windowWidth - GAP) {
          containerLeft =
            elementRect.left - containerWidth - GAP - parentOffset.left;
        }
      } else {
        containerLeft =
          elementRect.left - containerWidth - GAP - parentOffset.left;
        if (containerLeft < GAP) {
          containerLeft = elementRect.right + GAP - parentOffset.left;
        }
      }
      // Final viewport clamp so the picker always stays fully visible.
      if (containerLeft < GAP) containerLeft = GAP;
      if (containerLeft + containerWidth > windowWidth - GAP) {
        containerLeft = windowWidth - containerWidth - GAP;
      }

      // Vertically center on the trigger, then clamp inside the viewport.
      let topPx =
        elementRect.top +
        elementRect.height / 2 -
        containerHeight / 2 +
        window.scrollY -
        parentOffset.top;
      const minTop = window.scrollY + GAP - parentOffset.top;
      const maxTop =
        window.scrollY +
        windowHeight -
        containerHeight -
        GAP -
        parentOffset.top;
      if (topPx < minTop) topPx = minTop;
      if (topPx > maxTop) topPx = maxTop;

      this.container.style.left = `${containerLeft}px`;
      this.container.style.right = 'auto';
      this.container.style.top = `${topPx}px`;
      return;
    }

    // Vertical positioning for 'down' / 'up'.
    let containerTop: number;
    if (placement === 'up') {
      containerTop =
        elementRect.top +
        window.scrollY -
        containerHeight -
        parentOffset.top;
      this.drops = 'up';
    } else {
      containerTop = elementRect.bottom + window.scrollY - parentOffset.top;
      if (drops !== 'auto') this.drops = 'down';
    }

    const parentRightEdge =
      this.parentEl === document.body
        ? windowWidth
        : (this.parentEl as HTMLElement).clientWidth +
          (this.parentEl as HTMLElement).offsetLeft;

    let containerLeft: number | 'auto' = 0;
    let containerRight: number | 'auto' = 'auto';

    if (this.opens === 'left') {
      containerRight = parentRightEdge - elementRect.right;
      if (containerWidth > elementRect.left) {
        containerLeft = GAP;
        containerRight = 'auto';
      }
    } else if (this.opens === 'center') {
      containerLeft =
        elementRect.left +
        elementRect.width / 2 -
        containerWidth / 2 -
        parentOffset.left;
      if (containerLeft < GAP) {
        containerLeft = GAP;
      } else if (containerLeft + containerWidth > windowWidth - GAP) {
        containerLeft = windowWidth - containerWidth - GAP;
      }
    } else {
      containerLeft =
        elementRect.left +
        elementRect.width / 2 -
        containerWidth / 2 -
        parentOffset.left;
      if (containerLeft < GAP) {
        containerLeft = GAP;
      } else if (containerLeft + containerWidth > windowWidth - GAP) {
        containerLeft = windowWidth - containerWidth - GAP;
      }
    }

    this.container.style.left =
      typeof containerLeft === 'number' ? `${containerLeft}px` : containerLeft;
    this.container.style.right =
      containerRight === 'auto' ? 'auto' : `${containerRight}px`;
    this.container.style.top = `${containerTop}px`;
  }

  private handleResize(): void {
    if (this.isShowing) {
      this.move();
    }
  }
  public destroy(): void {
    this.container.remove();
    // Drop the legacy-scoped host too, otherwise an empty <div> leaks into
    // <body> for every picker that mounts.
    this.legacyHost?.remove();
    this.element.remove();
    this.isShowing = false;
  }
}

export default DateRangePicker;
